import { HttpInterceptorFn } from '@angular/common/http';
import { BehaviorSubject, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { inject } from '@angular/core';
import { getLabelMapper } from '../../functions/label-mapper';
import { TranslationService } from '@waseel-asp/tpa-waseel-lib-staging';
import { Operation, RecordStatus } from '../../constants/error-status-enum';
import { Store } from '@ngxs/store';
import { Auth } from '../../store/auth/auth.action';
import { AuthState } from '../../store/auth/auth.state';
import { NavigationEnd, Router } from '@angular/router';
import { HelperService } from '../../services/common/helper.service';
import { MatDialog } from '@angular/material/dialog';
import { LOCAL_STORAGE } from '../../constants/localstorage-enum';
import { API, API_ROOT_PATH } from '../../constants/api-url';
import { SKIP_ERROR_TOASTY } from '../../constants/system-constants.model';
import { AuthService } from '../../services/auth/auth.service';
import { resetPasswordConfig, resetUserPassword } from '../../../core/modules/admin-modules/public/reset-password/models/reset-password.component.models';
import { ToastrHelperService } from '../../services/common/toast-helper.service';
import { LoaderService } from '../../services/loader/loader.service';

const refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
let isRefreshing = false;
let isLoginPage = false;

export const HttpErrorInterceptor: HttpInterceptorFn = (request, next) => {
  const toastrHelperService = inject(ToastrHelperService);
  const translation = inject(TranslationService);
  const helperService = inject(HelperService);
  const store = inject(Store);
  const router = inject(Router);
  const dialog = inject(MatDialog);
  const authService = inject(AuthService);
  const loaderService = inject(LoaderService);
  const isAuthenticationApi = request.url.toLowerCase().startsWith(API_ROOT_PATH.AUTHENTICATION.toLowerCase());
  router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => isLoginPage = ([authService.getLoginPagePath(), authService.getLoginPagePath(true)].includes(router.url)));

  return next(request).pipe(
    catchError((errorResp) => {

      if (!navigator.onLine) {
        loaderService.hide();
        toastrHelperService.showErrorMessage('COMMON.NO_INTERNET_HTTP_REQUEST');
        return throwError(() => new Error("OFFLINE"));
      }

      if (request.context.get(SKIP_ERROR_TOASTY))
        return throwError(errorResp);

      if ((errorResp.status === 401) && !isRefreshing && !isLoginPage) {
        // Check if refresh token is expired
        if (authService.isRefreshTokenExpired()) {
          toastrHelperService.showErrorMessage('COMMON.NOT_AUTHORIZED_FOR_RESOURCE');
          dialog.closeAll();
          store.dispatch(new Auth.LogoutSuccess(store.selectSnapshot(AuthState.isPatientUser)));  // Redirect to login or show an error
          handleSessionExpired(router, authService);
          return throwError(errorResp);
        }

        isRefreshing = true;
        refreshTokenSubject.next(null);

        return store.dispatch(new Auth.RefreshToken()).pipe(
          switchMap(() => {
            // Retrieve the new token from the state after it has been updated
            const newToken = store.selectSnapshot(AuthState.token);
            refreshTokenSubject.next(newToken);
            isRefreshing = false;
            // Clone the request with the new token
            return next(helperService.cloneRequestWithNewToken(request, newToken));
          }),
          catchError((error) => {
            isRefreshing = false;
            refreshTokenSubject.next(null);
            toastrHelperService.showErrorMessage('COMMON.SOMETHING_WENT_WRONG');
            store.dispatch(new Auth.LogoutSuccess(store.selectSnapshot(AuthState.isPatientUser)));
            handleSessionExpired(router, authService);
            return throwError(error);
          })
        );
      } else if (!isAuthenticationApi && errorResp.status === 401 && isRefreshing) {
        // Wait for the new token if a refresh is already in progress
        return refreshTokenSubject.pipe(
          filter(token => token != null),
          take(1),
          switchMap((token) => {
            return next(helperService.cloneRequestWithNewToken(request, token));
          })
        );
      } else {
        let errorMessage = translation.instant('COMMON.SOMETHING_WENT_WRONG');
        if (isAuthenticationApi) {
          if (errorResp.status === 429) {
            errorMessage = translation.instant('COMMON.TOO_MANY_LOGIN_ATTEMPT');
            toastrHelperService.showErrorMessage(errorMessage);
          } else {
            const errorMsg = JSON.parse(errorResp?.error?.message ?? '{}');
            if (errorMsg && Object.keys(errorMsg).length > 0) {
              if (errorMsg.recordStatus == RecordStatus.Failed && errorMsg.message.includes('No connection could be made because the target machine actively refused it'))
                errorMsg.message = translation.instant('COMMON.AUTH_SERVICE_FAILED');
              showError(errorMsg, toastrHelperService, translation);
            } else
              toastrHelperService.showErrorMessage(errorResp?.error?.error || errorMessage);
          }
          return throwError(() => new Error(JSON.stringify(errorMessage)));
        } else {
          if (errorResp.status === 409)
            errorMessage = errorResp?.error?.errorMessage;
          else if (errorResp.status === 400)
            defaultErrorMessage(toastrHelperService);
          else if (errorResp.status === 403)
            return unAuthorisedErrorMessage(toastrHelperService, translation);
          else if (errorResp.status === 422) {
            const url = request.url.toLowerCase();
            if (
              url.startsWith(API.COMMON.USER.toLowerCase()) ||
              url.startsWith(API.COMMON.ROLES.toLowerCase()) ||
              url.startsWith(API.COMMON.GET_ALL_USERS.toLowerCase()) ||
              url.startsWith(API.COMMON.PERMISSIONS.toLowerCase()) ||
              url.startsWith(API.COMMON.RESET_PASSWORD.toLowerCase())
            ) {
              showKeyClockError(errorResp, toastrHelperService, helperService);
              return throwError(() => new Error(JSON.stringify(errorMessage)));
            }
            return businessRuleErrorMessage(errorResp, toastrHelperService, helperService, translation);
          } else {
            if (errorResp?.error?.message) {
              const errorMessage2 = !helperService.isValidJson(errorResp?.error?.message) ? errorResp?.error?.message : JSON.parse(errorResp?.error?.message);
              if (errorMessage2?.message && errorResp.status === 500) {
                defaultErrorMessage(toastrHelperService);
                return throwError(() => new Error(JSON.stringify(errorMessage2)));
              } else
                errorMessage = errorMessage2;
            } else
              defaultErrorMessage(toastrHelperService);
          }
          showError(errorMessage, toastrHelperService, translation);
          return throwError(() => new Error(JSON.stringify(errorMessage)));
        }
      }
    })
  );
};

function showKeyClockError(error, toastrHelperService, helperService: HelperService) {
  let errorMessage = '';
  const errorResult = !helperService.isValidJson(error?.error?.message) ? error?.error?.message : JSON.parse(error?.error?.message);
  const innerMessage = !helperService.isValidJson(errorResult?.message) ? errorResult?.message : JSON.parse(errorResult?.message);
  const parsedErrorMessage = !helperService.isValidJson(innerMessage) ? innerMessage : JSON.parse(innerMessage?.errorMessage);
  const parsedDescription = !helperService.isValidJson(innerMessage) ? innerMessage : JSON.parse(innerMessage?.error_description);
  const parsedError = !helperService.isValidJson(innerMessage) ? innerMessage : JSON.parse(innerMessage?.error);

  if (parsedErrorMessage || parsedDescription || parsedError) {
    errorMessage = (
      parsedDescription?.error_description || // OAuth-related errors
      parsedErrorMessage?.errorMessage ||      // Custom Keycloak errors
      parsedError?.error ||             // General error field
      innerMessage?.message ||                  // Default fallback message
      innerMessage
    );
  } else if (innerMessage)
    errorMessage = innerMessage?.message || innerMessage;

  toastrHelperService.showErrorMessage(errorMessage);
}

function showError(errorMessage, toastrHelperService, translation) {
  if (errorMessage.operation == Operation.BulkImport)
    showErrorForExcel(errorMessage, toastrHelperService, translation);
  else {
    switch (errorMessage.recordStatus) {
      case RecordStatus.NotFound:
        notFoundError(errorMessage, toastrHelperService, translation);
        break;
      case RecordStatus.AlreadyDeleted:
        notFoundError(errorMessage, toastrHelperService, translation);
        break;
      case RecordStatus.ReferenceExist:
        referenceError(errorMessage, toastrHelperService, translation);
        break;
      case RecordStatus.Failed:
        failedError(errorMessage, toastrHelperService);
        break;
      default:
        defaultErrorMessage(toastrHelperService);
        break;
    }
  }
}

function showErrorForExcel(error, toastrHelperService, translation) {
  const message = error.recordStatus == RecordStatus.NoContent ? 'COMMON.NO_CONTENT' : 'COMMON.NO_SHEET_FOUND';
  const labels = getLabelMapper(error.entityType);
  const entityName = translation.instant(labels['entityname']);
  error.message = translation.instant(message, { name: entityName });
  toastrHelperService.showErrorMessage(error.message);
}

function referenceError(error, toastrHelperService, translation) {
  const labels = getLabelMapper(error.entityType);
  const fieldNames = [];

  if (error.operation == Operation.Delete)
    toastrHelperService.showErrorMessage(translation.instant("COMMON.ACTIVE_ENTITY_DELETE_ERROR", { entity: translation.instant(labels['entityname']) }));

  error.listofFields.forEach(element => {
    const value = labels[element.toLowerCase()];
    value && fieldNames.push(translation.instant(value));
  });
  const field = fieldNames.length > 1 ? translation.instant('FORM_FIELD.FIELDS') : translation.instant('FORM_FIELD.FIELD');
  const entityName = translation.instant(labels['entityname']);
  error.message = translation.instant('COMMON.ERROR_MESSAGE_WITH_FIELDS', { entity: entityName, field: field, fieldNames: fieldNames.join(', ') });
  toastrHelperService.showErrorMessage(error.message);
}

function notFoundError(error, toastrHelperService, translation) {
  if ([resetPasswordConfig.entityName, resetUserPassword.entityName].includes(error.entityType))
    error.message = translation.instant('COMMON.RESET_PASSWORD_INVALID_REQUEST');
  else {
    const labels = getLabelMapper(error.entityType);
    const entityName = translation.instant(labels['entityname']);
    error.message = translation.instant('COMMON.ENTITY_NOT_FOUND', { entity: entityName });
  }
  toastrHelperService.showWarningMessage(error.message);
}

function failedError(error, toastrHelperService) {
  toastrHelperService.showErrorMessage(error.message);
}

function defaultErrorMessage(toastrHelperService) {
  toastrHelperService.showErrorMessage('COMMON.SOMETHING_WENT_WRONG');
}

function unAuthorisedErrorMessage(toastrHelperService, translation) {
  const errorMsg = translation.instant('COMMON.NOT_AUTHORIZED_FOR_RESOURCE');
  toastrHelperService.showErrorMessage(errorMsg);
  return throwError(() => new Error(JSON.stringify(errorMsg)));
}

function businessRuleErrorMessage(error, toastrHelperService, helperService: HelperService, translation) {
  const errorResult = JSON.parse(error.error.message);
  if (errorResult?.listofFields) {
    showError(errorResult, toastrHelperService, translation);
    return throwError(() => new Error(JSON.stringify(errorResult)));
  }
  const innerMessage = !helperService.isValidJson(errorResult.message) ? errorResult.message : JSON.parse(errorResult.message);
  toastrHelperService.showErrorMessage(innerMessage.message || innerMessage);
  return throwError(() => new Error(JSON.stringify(innerMessage.message)));
}

function handleSessionExpired(router, authService: AuthService) {
  localStorage.removeItem(LOCAL_STORAGE.AUTH_DETAILS);
  localStorage.removeItem(LOCAL_STORAGE.CURRENT_USER);
  localStorage.setItem(LOCAL_STORAGE.LAST_URL, router.url);
  authService.redirectToLoginPage();
}
